import { Link } from '@design-system/Link';
import { Image } from '@design-system/Image';
import { Badge } from '@design-system/Badge';
import { cn } from '@/utils/styles';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { Card, CardContent } from '@design-system/Card';
import { getFormulaImageUrl } from '@/utils/nutrition/getFormulaImageUrl';
import { SMP_PATH, SMP_DETAIL_PATH } from '@/constants/paths';
import { isEligibleForComponentization } from '@/utils/componentizationEligibility';
import { useIsComponentizationEnabled } from '@/hooks/useIsComponentizationEnabled';
import { useComponentizationEligibleSkus } from '@/hooks/useComponentizationEligibleSkus';
import type { Subscription } from '@viome/viome-base/src/usecase/Misc/MiscModel';

const SubscriptionStatus = {
  Active: 'Active',
  Cancelled: 'Cancelled',
  Paused: 'Paused',
} as const;

const getGradientClassByProduct = (imageUrl: string) => {
  const filename = imageUrl.split('/').pop() || '';

  switch (filename) {
    case 'gut_formula.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#EFE6B6_0%,#F0E9E3_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    case 'toothpaste_and_gel.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#CCE3E8_0%,#EEE9E1_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    case 'supplement.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#D5F6D9_0%,#EBEDE0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    case 'oral_lozenges.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#DCF3FF_0%,#E0E2F0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    default:
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#ccc_0%,#EBEDE0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
  }
};

// image_url may be a full URL (kit) or a relative filename (supplement).
const getProductImageUrl = (imageUrl: string) =>
  imageUrl?.startsWith('http') ? imageUrl : getFormulaImageUrl(imageUrl);

interface Props {
  subscription: Subscription;
}

export const AccountSubscriptionCard = ({ subscription }: Props) => {
  const isComponentizationEnabled = useIsComponentizationEnabled();
  const eligibleSkus = useComponentizationEligibleSkus();
  const targetPath =
    isComponentizationEnabled &&
    isEligibleForComponentization(subscription, eligibleSkus)
      ? SMP_DETAIL_PATH
      : SMP_PATH;
  const isKit = (subscription.category ?? '').toUpperCase() === 'KIT';
  const products =
    subscription.products?.filter((product) => product.is_last_subscribed) || [];
  const productCount = products.length;
  const status = subscription.subscription_status;

  const title =
    subscription.sku_name || subscription.sku || 'My Subscription';

  // Hardcoded until BE returns plan duration.
  const supplyText = isKit ? 'Annual supply' : '30-day supply';
  
  const tileClass = isKit ? 'size-16' : 'size-16';
  const imageClass = isKit ? 'size-20' : 'size-10';

  const renderProducts = () => {
    if (productCount === 0) {
      return null;
    }
    if (productCount === 1) {
      const product = products[0];
      return (
        <div
          className="flex items-center gap-4"
          data-testid="accountSubscription-productDetail"
        >
          <div
            className={cn(
              'relative rounded-md overflow-hidden flex items-center justify-center',
              tileClass,
              getGradientClassByProduct(product.image_url),
            )}
          >
            <Image
              src={getProductImageUrl(product.image_url)}
              alt={product.name}
              className={cn('object-contain', imageClass)}
            />
          </div>
          <div className="flex flex-col">
            <span className="typography-body text-primary">{product.name}</span>
            <span className="typography-body text-secondary">{supplyText}</span>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-4" data-testid="accountSubscription-productList">
        <div className="flex flex-wrap gap-2">
          {products.slice(0, 3).map((product) => (
            <div
              key={product.id}
              className={cn(
                'relative rounded-md overflow-hidden flex items-center justify-center',
                tileClass,
                getGradientClassByProduct(product.image_url),
              )}
            >
              <Image
                src={getProductImageUrl(product.image_url)}
                alt={product.name}
                className={cn('object-contain', imageClass)}
              />
            </div>
          ))}
        </div>
        <span className="typography-body text-primary">{productCount} items</span>
      </div>
    );
  };

  return (
    <Link
      to={`${targetPath}?subscriptionId=${subscription.subscription_id}`}
      variant="flat"
      className="block"
      data-testid="accountSubscription-card">
      <Card className="border border-stroke-static">
        <CardContent className="flex flex-col gap-6 p-6">
          <div className="flex items-start justify-between gap-2">
            <span className="typography-h2 text-primary">{title}</span>
            {status && (
              <Badge
                size="sm"
                className={cn(
                  'capitalize shrink-0',
                  status === SubscriptionStatus.Active && 'bg-[#009E82] text-white',
                  status === SubscriptionStatus.Cancelled && 'bg-tertiary text-white',
                  status === SubscriptionStatus.Paused && 'bg-black text-white',
                )}
              >
                {status}
              </Badge>
            )}
          </div>

          {renderProducts()}

          <div className="flex items-center justify-between">
            <span className="typography-body text-primary">Manage subscription</span>
            <ChevronRightIcon className="size-6 text-tertiary" aria-hidden />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};